import { Status, snackbarService } from '@one/web-components';
import { APP_THEME } from 'core/theme/theme.config';

// A modal <dialog> makes everything outside its subtree inert, so the drawer must live inside the
// topmost open dialog to keep toasts visible and clickable. ODSU owns the drawer and creates it under
// document.body; moving that same node into a modal and back preserves its active toasts.
const NOTIFICATION_DRAWER = 'owc-notification-drawer';

const topmostOpenModal = (): HTMLElement | null =>
    Array.from(document.querySelectorAll<HTMLDialogElement>('dialog[open]')).findLast((dialog) =>
        dialog.matches(':modal')
    ) ?? null;

// Kept so the drawer is still reachable after a closing dialog detaches it from the document.
let managedDrawer: HTMLElement | null = null;

const homeNotificationDrawer = (isRelocation = false) => {
    const drawer = document.querySelector<HTMLElement>(NOTIFICATION_DRAWER) ?? managedDrawer;
    if (!drawer) {
        return;
    }
    managedDrawer = drawer;
    const target: HTMLElement = topmostOpenModal() ?? document.body;
    if (drawer.parentElement === target) {
        return;
    }
    if (isRelocation) {
        // Re-inserting replays the slide-in animation, so a moved toast looks like it appears twice.
        drawer.querySelectorAll<HTMLElement>('owc-snackbar').forEach((snackbar) => {
            snackbar.classList.add('poc-toast-relocated');
        });
    }
    target.appendChild(drawer);
};

let drawerObserver: MutationObserver | undefined;

// Gates the body-wide observer so unrelated renders skip the relocation work. A removed/added
// subtree may wrap the <dialog>, so also look inside it.
const involvesDialog = (mutation: MutationRecord): boolean => {
    if (mutation.type === 'attributes') {
        return (mutation.target as Element).matches?.('dialog') ?? false;
    }
    return [...mutation.addedNodes, ...mutation.removedNodes].some(
        (node) => node instanceof Element && (node.matches('dialog') || node.querySelector('dialog') !== null)
    );
};

const keepDrawerAboveModalOverlay = () => {
    homeNotificationDrawer();
    if (drawerObserver) {
        return;
    }
    drawerObserver = new MutationObserver((mutations) => {
        if (mutations.some(involvesDialog)) {
            homeNotificationDrawer(true);
        }
    });
    drawerObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['open'],
    });
};

const showNotification = (message: string, status: Exclude<Status, 'inherit' | 'active'>) => {
    snackbarService.show({
        message,
        snackbarProps: {
            status,
            direction: 'horizontal',
            verticalPlacement: 'top',
            horizontalPlacement: 'right',
            duration: 5000,
            showClose: true,
            theme: APP_THEME,
        },
    });
    keepDrawerAboveModalOverlay();
};

export const showSuccessNotification = (message: string) => showNotification(message, 'positive');
export const showInfoNotification = (message: string) => showNotification(message, 'info');
export const showErrorNotification = (message: string) => showNotification(message, 'alarm');
